import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useState } from 'react'

import LinedTextArea from '../../components/tools/LinedTextArea'
import ToolWorkspace from '../../components/tools/ToolWorkspace'
import {
  noopToolSwap,
  type ToolAbout,
  TOOL_CODEC_SELECT_CLASS,
  ToolIoChip,
  WrapLinesPanelSwitch,
} from '../../components/tools/toolChrome'
import {
  DEFAULT_HASH_CODEC_ID,
  getCodec,
  HASH_CODECS,
  isHashCodecId,
  MAX_CODEC_INPUT_CHARS,
  type HashCodecId,
} from '../../lib/codecs'

const HASH_ABOUT: ToolAbout = {
  description:
    'Compute SHA-1, SHA-256, SHA-384, or SHA-512 digests from the UTF-8 encoding of your text. You get lowercase hex. Hashing runs one way only, so there is no decode mode. Web Crypto in the browser does the digest.',
  libraries: [
    {
      name: 'MDN: SubtleCrypto.digest',
      href: 'https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest',
    },
    { name: 'MDN: TextEncoder', href: 'https://developer.mozilla.org/en-US/docs/Web/API/TextEncoder' },
  ],
}

export default function HashTool() {
  const [codecId, setCodecId] = useState<HashCodecId>(DEFAULT_HASH_CODEC_ID)
  const [mode, setMode] = useState<'encode' | 'decode'>('encode')
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lineWrap, setLineWrap] = useState(false)

  const codec = useMemo(() => getCodec(codecId) ?? HASH_CODECS[0], [codecId])

  const run = useCallback(async () => {
    try {
      setError(null)
      if (input.length > MAX_CODEC_INPUT_CHARS) {
        setError(`Input exceeds ${MAX_CODEC_INPUT_CHARS.toLocaleString()} characters (browser safety limit).`)
        return
      }
      setBusy(true)
      const out = await Promise.resolve(codec.encode(input))
      setOutput(out)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Hash failed')
    } finally {
      setBusy(false)
    }
  }, [input, codec])

  const copy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
    } catch {
      setError('Could not copy to clipboard')
    }
  }, [output])

  const onCodecChange = useCallback((id: string) => {
    if (!isHashCodecId(id)) return
    setCodecId(id)
    setOutput('')
    setError(null)
  }, [])

  const inputColumn = (
    <LinedTextArea
      fillHeight
      value={input}
      onChange={(e) => setInput(e.target.value.slice(0, MAX_CODEC_INPUT_CHARS))}
      maxLength={MAX_CODEC_INPUT_CHARS}
      rows={14}
      wrapLines={lineWrap}
      placeholder={codec.placeholderEncode}
      aria-label="Hash input"
    />
  )

  const toolSettings = <WrapLinesPanelSwitch lineWrap={lineWrap} onLineWrapChange={setLineWrap} />

  return (
    <>
      <Head title="Hash (digests)" />
      <ToolWorkspace
        heading="Cryptographic hashes"
        inputHeaderStart={<ToolIoChip>INPUT (RAW)</ToolIoChip>}
        algorithmSelector={
          <select
            value={codecId}
            onChange={(e) => onCodecChange(e.target.value)}
            aria-label="Algorithm"
            className={TOOL_CODEC_SELECT_CLASS}
          >
            {HASH_CODECS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        }
        toolSettings={toolSettings}
        inputColumn={inputColumn}
        mode={mode}
        setMode={setMode}
        encodeOnly
        swapDisabled
        swapTitle="Not available"
        onSwap={noopToolSwap}
        onExecute={run}
        executeBusy={busy}
        output={output}
        onCopy={copy}
        error={error}
        statsCharValue={String(input.length).padStart(6, '0')}
        statsAlgorithm={codec.statsLabel}
        statsOutputLen={String(output.length).padStart(6, '0')}
        statsModeLabel="DIGEST"
        outputLineNumbers
        lineWrap={lineWrap}
        about={HASH_ABOUT}
      />
    </>
  )
}
